import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import type { MapDef, MapSign, PoiKind } from "@rietz/shared";
import {
  AO_BOTTOM,
  AO_SIDE,
  AO_TOP,
  POI_BOARD_COLOR,
  POI_BOARD_EDGE,
  POI_BOARD_GRAIN,
  POI_BOARD_INK,
  SIGN_POST_METAL,
  SIGN_POST_WOOD,
  STREET_PLATE_COLOR,
  STREET_PLATE_EDGE,
  STREET_PLATE_INK,
  groundDarkening,
  randSym,
} from "./palette.js";
import { cssHex, poiColor } from "./poiPalette.js";
import { SHADOWS_ENABLED } from "./shadows.js";
import {
  ATLAS_GUTTER_PX,
  ATLAS_MAX_PX,
  SIGN_STYLES,
  packAtlas,
  planPlate,
  signKey,
  styleFont,
  type AtlasCell,
  type AtlasItem,
  type PlatePlan,
  type SignStyle,
} from "./signLayout.js";

/**
 * Physical signposts: street-name plates at the corners and place boards
 * outside named buildings — the diegetic half of the signposting the POI
 * sprites (`game/poiLabels.ts`) do from above. A village emits 30–150, built as
 * ONE vertex-coloured mesh (posts and plate bodies) plus ONE mesh per atlas
 * page (every plate FACE as flat quads on one canvas texture): two draw calls
 * for a hundred signs. Identical text shares one atlas cell. The back face's
 * UVs are flipped in u so the text reads from behind. Headless, the layer
 * degrades to bare posts — `signCount` still counts them.
 */

export interface BuiltSigns {
  group: THREE.Group;
  /** Signposts actually built. 0 for a map with no `signs`. */
  signCount: number;
  /** 1 for the merged posts/bodies, plus one per atlas page. */
  drawCalls: number;
  triangles: number;
  dispose(): void;
}

/** Distance the textured face floats off the plate body, metres. */
const FACE_OFFSET_M = 0.002;

/**
 * Reads `map.signs` defensively — a newer schema degrades to fewer signs, never
 * a throw. Exported for the smoke test.
 */
export function mapSigns(map: MapDef): MapSign[] {
  const raw = (map as { signs?: unknown }).signs;
  if (!Array.isArray(raw)) return [];

  const out: MapSign[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const e = entry as Partial<MapSign>;
    if (typeof e.text !== "string" || e.text.length === 0) continue;
    if (e.kind !== "street" && e.kind !== "poi") continue;
    const pos = e.pos;
    if (
      typeof pos !== "object" ||
      pos === null ||
      !Number.isFinite(pos.x) ||
      !Number.isFinite(pos.y) ||
      !Number.isFinite(pos.z)
    ) {
      continue;
    }
    const sign: MapSign = {
      text: e.text,
      kind: e.kind,
      pos,
      yaw: Number.isFinite(e.yaw) ? (e.yaw as number) : 0,
    };
    if (e.kind === "poi" && typeof e.poiKind === "string") {
      sign.poiKind = e.poiKind as PoiKind;
    }
    out.push(sign);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

interface Planned {
  sign: MapSign;
  style: SignStyle;
  key: string;
  plan: PlatePlan;
}

export function buildSigns(map: MapDef): BuiltSigns {
  const group = new THREE.Group();
  group.name = `signs:${map.id}`;

  const signs = mapSigns(map);
  const empty: BuiltSigns = {
    group,
    signCount: 0,
    drawCalls: 0,
    triangles: 0,
    dispose: () => {},
  };
  if (signs.length === 0) return empty;

  // --- Plate sizing (needs a canvas to measure text) -----------------------
  const measureCtx = makeContext(1, 1);
  const planned: Planned[] = [];
  const plans = new Map<string, PlatePlan>();
  if (measureCtx !== null) {
    for (const sign of signs) {
      const style = SIGN_STYLES[sign.kind];
      const key = signKey(sign.kind, sign.text, sign.poiKind);
      let plan = plans.get(key);
      if (plan === undefined) {
        const font = styleFont(style);
        measureCtx.font = font;
        plan = planPlate(sign.text, style, (s) => {
          measureCtx.font = font;
          return measureCtx.measureText(s).width;
        });
        plans.set(key, plan);
      }
      planned.push({ sign, style, key, plan });
    }
  }

  // --- Atlas ---------------------------------------------------------------
  const items: AtlasItem[] = [];
  for (const [key, plan] of plans) {
    items.push({ key, w: plan.cellW, h: plan.cellH });
  }
  const packed = packAtlas(items, ATLAS_MAX_PX, ATLAS_GUTTER_PX);
  const cells = new Map<string, AtlasCell>();
  for (const cell of packed.cells) cells.set(cell.key, cell);

  const textures: THREE.CanvasTexture[] = [];
  for (let p = 0; p < packed.pages.length; p++) {
    const page = packed.pages[p];
    if (page === undefined) continue;
    const ctx = makeContext(page.width, page.height);
    if (ctx === null) break;
    const drawn = new Set<string>();
    for (const item of planned) {
      const cell = cells.get(item.key);
      if (cell === undefined || cell.page !== p || drawn.has(item.key)) continue;
      drawn.add(item.key);
      drawCell(ctx, cell, item);
    }
    const tex = new THREE.CanvasTexture(ctx.canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.magFilter = THREE.LinearFilter;
    // Mipmapped: a 2 m plate is ~20 px tall at 30 m against a 350 px cell.
    // `ATLAS_GUTTER_PX` keeps the mips from smearing one name into the next.
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.generateMipmaps = true;
    tex.needsUpdate = true;
    textures.push(tex);
  }
  const pageCount = textures.length;

  // --- Bodies: posts + plate blanks, one merged vertex-coloured mesh -------
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < signs.length; i++) {
    const sign = signs[i];
    if (sign === undefined) continue;
    const style = SIGN_STYLES[sign.kind];
    const isStreet = sign.kind === "street";

    const post = new THREE.BoxGeometry(
      style.postWidthM,
      style.postHeightM,
      style.postWidthM,
    );
    post.translate(0, style.postHeightM * 0.5, 0);
    parts.push(
      place(
        paintBox(post, isStreet ? SIGN_POST_METAL : SIGN_POST_WOOD, i),
        sign,
      ),
    );

    // Only signs that got a measured plate get a plate body: a headless build
    // has no text to put on one, and a blank board is worse than a bare post.
    const plan = plans.get(signKey(sign.kind, sign.text, sign.poiKind));
    if (plan === undefined || pageCount === 0) continue;
    const blank = new THREE.BoxGeometry(
      plan.widthM,
      plan.heightM,
      style.plateThicknessM,
    );
    blank.translate(0, style.plateCenterYM, 0);
    parts.push(
      place(
        paintBox(blank, isStreet ? STREET_PLATE_EDGE : POI_BOARD_EDGE, i + 977),
        sign,
      ),
    );
  }

  const bodies = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (bodies === null) throw new Error("buildSigns: geometry merge failed");
  bodies.computeBoundingSphere();

  const bodyMaterial = new THREE.MeshLambertMaterial({
    vertexColors: true,
    flatShading: true,
  });
  const bodyMesh = new THREE.Mesh(bodies, bodyMaterial);
  bodyMesh.name = "sign-bodies";
  bodyMesh.frustumCulled = false;
  // Under `?shadows=1` only. Posts and boards cast and receive; the text quads
  // do
  // NOT cast — a second caster in the same millimetre buys only depth fighting.
  if (SHADOWS_ENABLED) {
    bodyMesh.castShadow = true;
    bodyMesh.receiveShadow = true;
  }
  group.add(bodyMesh);

  let triangles = indexedTriangles(bodies);

  // --- Faces: two textured quads per plate, one mesh per atlas page --------
  const faceMeshes: THREE.Mesh[] = [];
  const faceMaterials: THREE.Material[] = [];
  for (let p = 0; p < pageCount; p++) {
    const page = packed.pages[p];
    const tex = textures[p];
    if (page === undefined || tex === undefined) continue;

    const forPage = planned.filter((e) => cells.get(e.key)?.page === p);
    if (forPage.length === 0) continue;

    const geo = faceGeometry(forPage, cells, page);
    const material = new THREE.MeshLambertMaterial({
      map: tex,
      // A plate's shaded side gets ambient only, and an unreadable street name
      // is
      // not doing its job — a trace of self-illumination, as retroreflective
      // paint.
      emissive: 0xffffff,
      emissiveMap: tex,
      emissiveIntensity: 0.18,
    });
    const mesh = new THREE.Mesh(geo, material);
    mesh.name = `sign-faces:${p}`;
    mesh.frustumCulled = false;
    // Receives but never casts: a street name that stayed sunlit while the
    // board behind it fell into a building's shadow would read as glowing.
    if (SHADOWS_ENABLED) mesh.receiveShadow = true;
    group.add(mesh);
    faceMeshes.push(mesh);
    faceMaterials.push(material);
    triangles += indexedTriangles(geo);
  }

  return {
    group,
    signCount: signs.length,
    drawCalls: 1 + faceMeshes.length,
    triangles,
    dispose(): void {
      group.remove(bodyMesh);
      bodies.dispose();
      bodyMaterial.dispose();
      for (const mesh of faceMeshes) {
        group.remove(mesh);
        mesh.geometry.dispose();
      }
      for (const m of faceMaterials) m.dispose();
      for (const t of textures) t.dispose();
      faceMeshes.length = 0;
      faceMaterials.length = 0;
      textures.length = 0;
      group.clear();
    },
  };
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

const bodyColor = new THREE.Color();

/**
 * Bakes flat vertex colours with `mapMesh.ts`'s fake AO: tops lit, sides mid,
 * undersides dark, the bottom half-metre fading to the ground. Reads LOCAL y —
 * height above the sign's base.
 */
function paintBox(
  geo: THREE.BufferGeometry,
  hex: number,
  seed: number,
): THREE.BufferGeometry {
  geo.deleteAttribute("uv");
  bodyColor.setHex(hex);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const nrm = geo.attributes.normal as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  // One nudge per box, not per vertex: a row of identical plates reads as
  // manufactured, but a row of identically *lit* posts reads as a texture bug.
  const wobble = 1 + randSym(seed, 0x51c0) * 0.05;
  for (let i = 0; i < pos.count; i++) {
    const ny = nrm.getY(i);
    const f =
      (ny > 0.5 ? AO_TOP : ny < -0.5 ? AO_BOTTOM : AO_SIDE) *
      groundDarkening(pos.getY(i)) *
      wobble;
    const o = i * 3;
    colors[o] = bodyColor.r * f;
    colors[o + 1] = bodyColor.g * f;
    colors[o + 2] = bodyColor.b * f;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geo;
}

/**
 * Moves a local geometry onto its sign. Yaw 0 faces −Z (normal `(−sin yaw, 0,
 * −cos yaw)`), so every geometry is authored readable-side toward −Z and a
 * `rotateY(yaw)` lands it.
 */
function place(geo: THREE.BufferGeometry, sign: MapSign): THREE.BufferGeometry {
  if (sign.yaw !== 0) geo.rotateY(sign.yaw);
  geo.translate(sign.pos.x, sign.pos.y, sign.pos.z);
  return geo;
}

/** Two quads per plate — front at −Z, back at +Z with its u axis mirrored. */
function faceGeometry(
  entries: readonly Planned[],
  cells: ReadonlyMap<string, AtlasCell>,
  page: { width: number; height: number },
): THREE.BufferGeometry {
  const quads = entries.length * 2;
  const positions = new Float32Array(quads * 4 * 3);
  const normals = new Float32Array(quads * 4 * 3);
  const uvs = new Float32Array(quads * 4 * 2);
  const indices = new Uint32Array(quads * 6);

  let v = 0;
  let t = 0;
  for (const entry of entries) {
    const cell = cells.get(entry.key);
    if (cell === undefined) continue;
    const { sign, style, plan } = entry;

    const u0 = cell.x / page.width;
    const u1 = (cell.x + cell.w) / page.width;
    // Canvas y runs down and `CanvasTexture` uploads with `flipY`, so the
    // cell's TOP edge is the higher v.
    const vTop = 1 - cell.y / page.height;
    const vBot = 1 - (cell.y + cell.h) / page.height;

    const hw = plan.widthM * 0.5;
    const yb = style.plateCenterYM - plan.heightM * 0.5;
    const yt = style.plateCenterYM + plan.heightM * 0.5;
    const hz = style.plateThicknessM * 0.5 + FACE_OFFSET_M;

    const cos = Math.cos(sign.yaw);
    const sin = Math.sin(sign.yaw);

    // Front (readable from −Z, i.e. from the direction the sign faces): the
    // viewer's right is −X there, so u grows toward −X.
    emitQuad(
      positions, normals, uvs, indices, v, t,
      sign, cos, sin, hw, yb, yt, -hz,
      -sin, -cos,
      [u1, vBot, u0, vBot, u0, vTop, u1, vTop],
      false,
    );
    v += 4;
    t += 6;

    // Back: same plate seen from +Z, where the viewer's right is +X. Mirroring
    // u is the whole trick — without it the text reads backwards.
    emitQuad(
      positions, normals, uvs, indices, v, t,
      sign, cos, sin, hw, yb, yt, hz,
      sin, cos,
      [u0, vBot, u1, vBot, u1, vTop, u0, vTop],
      true,
    );
    v += 4;
    t += 6;
  }

  // An entry whose cell went missing is skipped above; the buffers are sized to
  // what was emitted, or a fan of degenerate triangles would sit on the origin.
  const geo = new THREE.BufferGeometry();
  const used = v === quads * 4;
  geo.setAttribute(
    "position",
    new THREE.BufferAttribute(used ? positions : positions.slice(0, v * 3), 3),
  );
  geo.setAttribute(
    "normal",
    new THREE.BufferAttribute(used ? normals : normals.slice(0, v * 3), 3),
  );
  geo.setAttribute(
    "uv",
    new THREE.BufferAttribute(used ? uvs : uvs.slice(0, v * 2), 2),
  );
  geo.setIndex(
    new THREE.BufferAttribute(used ? indices : indices.slice(0, t), 1),
  );
  geo.computeBoundingSphere();
  return geo;
}

/** Scratch corners, in the plate's own frame. `emitQuad` allocates nothing. */
const cornerX = [0, 0, 0, 0];
const cornerY = [0, 0, 0, 0];

/**
 * One plate face. `plusZ` says which way the normal points in the plate's
 * frame; the −Z face needs reversed winding. Corners laid out and rotated by
 * hand — one cos/sin pair per plate beats merging 300 geometries.
 */
function emitQuad(
  positions: Float32Array,
  normals: Float32Array,
  uvs: Float32Array,
  indices: Uint32Array,
  v: number,
  t: number,
  sign: MapSign,
  cos: number,
  sin: number,
  hw: number,
  yb: number,
  yt: number,
  lz: number,
  nx: number,
  nz: number,
  uv: readonly number[],
  plusZ: boolean,
): void {
  cornerX[0] = -hw; cornerX[1] = hw; cornerX[2] = hw; cornerX[3] = -hw;
  cornerY[0] = yb; cornerY[1] = yb; cornerY[2] = yt; cornerY[3] = yt;
  for (let i = 0; i < 4; i++) {
    const x = cornerX[i] as number;
    const o = (v + i) * 3;
    // Rotation about +Y: x' = x·cos + z·sin, z' = −x·sin + z·cos.
    positions[o] = sign.pos.x + x * cos + lz * sin;
    positions[o + 1] = sign.pos.y + (cornerY[i] as number);
    positions[o + 2] = sign.pos.z - x * sin + lz * cos;
    normals[o] = nx;
    normals[o + 1] = 0;
    normals[o + 2] = nz;
    const uo = (v + i) * 2;
    uvs[uo] = uv[i * 2] as number;
    uvs[uo + 1] = uv[i * 2 + 1] as number;
  }
  if (plusZ) {
    indices[t] = v; indices[t + 1] = v + 1; indices[t + 2] = v + 2;
    indices[t + 3] = v; indices[t + 4] = v + 2; indices[t + 5] = v + 3;
  } else {
    indices[t] = v; indices[t + 1] = v + 2; indices[t + 2] = v + 1;
    indices[t + 3] = v; indices[t + 4] = v + 3; indices[t + 5] = v + 2;
  }
}

function indexedTriangles(geo: THREE.BufferGeometry): number {
  const index = geo.getIndex();
  const verts = index !== null ? index.count : (geo.attributes.position?.count ?? 0);
  return verts / 3;
}

// ---------------------------------------------------------------------------
// Canvas
// ---------------------------------------------------------------------------

/** `null` without a DOM — every caller treats that as "no plates", not error. */
function makeContext(
  width: number,
  height: number,
): CanvasRenderingContext2D | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas.getContext("2d");
}

/**
 * Paints one plate into the atlas. The background is flooded a gutter's width
 * beyond the cell so the texels the mip chain averages across the boundary are
 * this plate's own colour.
 */
function drawCell(
  ctx: CanvasRenderingContext2D,
  cell: AtlasCell,
  entry: Planned,
): void {
  const { style, plan, sign } = entry;
  const g = ATLAS_GUTTER_PX;
  const { x, y, w, h } = cell;

  const bg = sign.kind === "street" ? STREET_PLATE_COLOR : POI_BOARD_COLOR;
  ctx.fillStyle = cssHex(bg);
  ctx.fillRect(x - g, y - g, w + g * 2, h + g * 2);

  if (sign.kind === "street") {
    // A hairline white keyline inset from the edge — the detail that makes an
    // Austrian plate read as enamel rather than as a blue rectangle.
    ctx.strokeStyle = cssHex(STREET_PLATE_INK);
    ctx.lineWidth = style.borderPx;
    const inset = style.borderPx * 1.5;
    ctx.strokeRect(x + inset, y + inset, w - inset * 2, h - inset * 2);
    ctx.fillStyle = cssHex(STREET_PLATE_INK);
  } else {
    // Grain, then a coloured strip along the bottom edge keyed to the POI
    // palette — the same amber that means "somewhere to eat" on the town map.
    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.strokeStyle = cssHex(POI_BOARD_GRAIN);
    ctx.lineWidth = 1;
    for (let i = 1; i <= 3; i++) {
      const gy = Math.round(y + (h * i) / 4) + 0.5;
      ctx.beginPath();
      ctx.moveTo(x + 2, gy);
      ctx.lineTo(x + w - 2, gy);
      ctx.stroke();
    }
    ctx.restore();

    ctx.fillStyle = cssHex(
      sign.poiKind === undefined ? POI_BOARD_INK : poiColor(sign.poiKind),
    );
    ctx.fillRect(
      x + style.borderPx,
      y + h - style.borderPx * 2,
      w - style.borderPx * 2,
      style.borderPx,
    );
    ctx.fillStyle = cssHex(POI_BOARD_INK);
  }

  ctx.font = styleFont(style);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(plan.text, x + w * 0.5, y + h * style.baselineFrac);
}
